import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  IconButton,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Autocomplete
} from '@mui/material';
import {
  Download,
  Inventory,
  Visibility,
  Close,
  Assessment,
  TableChart,
  CloudUpload,
  CompareArrows,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AccessTime,
  CalendarMonth,
  ExpandMore,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
  Add,
  Restore,
  AccountTree,
  Search
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isAfter, isEqual } from 'date-fns';
import { manufacturingAPI } from '../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';
import CustomReportsSection from './reports/CustomReportsSection';
import AlwaysIncludeCodesField from './reports/AlwaysIncludeCodesField';
import useAlwaysInclude from './reports/useAlwaysInclude';

// Reports whose output always includes the shared picked items / ticked code sheets.
const ALWAYS_INCLUDE_REPORTS = [
  'stock-report', 'stock-movements', 'inventory-value', 'bom-usage', 'raw-material-usage',
];

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // File upload state for adjustment report
  const [uploadedFile, setUploadedFile] = useState(null);
  const [adjustmentPreview, setAdjustmentPreview] = useState(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  
  // Inventory Change Report state
  const [inventoryChangeDialogOpen, setInventoryChangeDialogOpen] = useState(false);
  const [inventoryChangePreview, setInventoryChangePreview] = useState(null);
  const [inventoryChangeDate, setInventoryChangeDate] = useState(new Date());
  const [inventoryChangeStartTime, setInventoryChangeStartTime] = useState(null);
  const [inventoryChangeEndTime, setInventoryChangeEndTime] = useState(null);
  const [inventoryChangeErrors, setInventoryChangeErrors] = useState({});

  // Stock Movements (date-range) Report state
  const [stockMovementsDialogOpen, setStockMovementsDialogOpen] = useState(false);
  const [stockMovementsPreview, setStockMovementsPreview] = useState(null);
  const [stockMovementsStartDate, setStockMovementsStartDate] = useState(null);
  const [stockMovementsEndDate, setStockMovementsEndDate] = useState(null);
  const [stockMovementsErrors, setStockMovementsErrors] = useState({});

  // Stock Take Import (upload-apply) state
  const [stockTakeImportDialogOpen, setStockTakeImportDialogOpen] = useState(false);
  const [stockTakeImportFile, setStockTakeImportFile] = useState(null);
  const [stockTakeImportPreview, setStockTakeImportPreview] = useState(null);
  const [stockTakeImportCountDate, setStockTakeImportCountDate] = useState(null);
  const [stockTakeImportAvailableSheets, setStockTakeImportAvailableSheets] = useState([]);
  const [stockTakeImportSelectedSheets, setStockTakeImportSelectedSheets] = useState([]);
  // item_code strings whose positive adjustment the user has chosen to skip. Default: include all.
  const [stockTakeImportExcludedCodes, setStockTakeImportExcludedCodes] = useState([]);
  // Unknown-code creation: codes the user has UNticked (won't be created) + the type to create as.
  const [stockTakeImportSkipCreateCodes, setStockTakeImportSkipCreateCodes] = useState([]);
  const [stockTakeImportCreateType, setStockTakeImportCreateType] = useState('raw_material');
  const stockTakeImportFileInputRef = useRef(null);

  // Undo last stock take state
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoing, setUndoing] = useState(false);

  // Transactions Since Stock Take (stocktake-since) state
  const [stockTakeSinceDialogOpen, setStockTakeSinceDialogOpen] = useState(false);
  const [stockTakeSinceStartDate, setStockTakeSinceStartDate] = useState(null);
  const [stockTakeSinceEndDate, setStockTakeSinceEndDate] = useState(null);
  const [stockTakeSincePreview, setStockTakeSincePreview] = useState(null);
  const [stockTakeSinceErrors, setStockTakeSinceErrors] = useState({});

  // Inventory Value on Date (single-date) state
  const [inventoryValueDialogOpen, setInventoryValueDialogOpen] = useState(false);
  const [inventoryValueDate, setInventoryValueDate] = useState(null);
  const [inventoryValuePreview, setInventoryValuePreview] = useState(null);
  const [inventoryValueErrors, setInventoryValueErrors] = useState({});
  const [inventoryValueOnlyCounted, setInventoryValueOnlyCounted] = useState(true);

  // BOM Cost Roll-Up (bom-search) state
  const [bomCostDialogOpen, setBomCostDialogOpen] = useState(false);
  const [bomCostQuery, setBomCostQuery] = useState('');
  const [bomCostOptions, setBomCostOptions] = useState([]);
  const [bomCostSearchLoading, setBomCostSearchLoading] = useState(false);
  const [bomCostSelected, setBomCostSelected] = useState(null);
  const [bomCostQuantity, setBomCostQuantity] = useState('1');
  const [bomCostPreview, setBomCostPreview] = useState(null);
  const [bomCostAllOnlyOrdered, setBomCostAllOnlyOrdered] = useState(false);
  const [bomCostAllMonth, setBomCostAllMonth] = useState(null);

  // BOM Usage by Month (bom-usage) state
  const [bomUsageDialogOpen, setBomUsageDialogOpen] = useState(false);
  const [bomUsageMonth, setBomUsageMonth] = useState(new Date());
  const [bomUsagePreview, setBomUsagePreview] = useState(null);

  // Raw Material Usage (raw-material-usage) state
  const [rawMaterialUsageDialogOpen, setRawMaterialUsageDialogOpen] = useState(false);
  const [rawMaterialUsageStartDate, setRawMaterialUsageStartDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [rawMaterialUsageEndDate, setRawMaterialUsageEndDate] = useState(new Date());
  const [rawMaterialUsagePreview, setRawMaterialUsagePreview] = useState(null);

  // Report options state
  const [reportOptions, setReportOptions] = useState({
    include_zero_stock: false,
    only_counted: true,
    include_raw_materials: false,
    include_subassemblies: false,
    include_finished: false,
    only_differences: true
  });

  // Items / code sheets each report always includes — a separate shared
  // server-side list per report (hooks must be called unconditionally, so
  // one instance per key, always).
  const alwaysInclude = {
    'stock-report': useAlwaysInclude('stock-report'),
    'stock-movements': useAlwaysInclude('stock-movements'),
    'inventory-value': useAlwaysInclude('inventory-value'),
    'bom-usage': useAlwaysInclude('bom-usage'),
    'raw-material-usage': useAlwaysInclude('raw-material-usage'),
  };
  const alwaysIncludeCodesFor = (reportId) => alwaysInclude[reportId]?.codes ?? '';

  usePageTitle('Reports');

  useEffect(() => {
    loadReports();
  }, []);

  const alwaysIncludeField = (reportId, compact = false) => (
    <AlwaysIncludeCodesField state={alwaysInclude[reportId]} compact={compact} />
  );

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await manufacturingAPI.reports.list();
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to load reports:', error);
      setReports([
        {
          id: 'stock-report',
          name: 'Stock Report (Sage Format)',
          description: 'Export current stock for sub-assemblies and raw materials used in a customer order, in the Stock Take import format. Only items counted in a stock take by default.',
          category: 'Inventory',
          type: 'download',
          options: [
            { key: 'only_counted', label: 'Only items counted in a stock take', type: 'boolean', default: true },
            { key: 'include_raw_materials', label: 'Also include raw materials counted in a stock take', type: 'boolean', default: false },
            { key: 'include_subassemblies', label: 'Also include sub-assemblies with stock', type: 'boolean', default: false },
            { key: 'include_finished', label: 'Also include finished goods with stock', type: 'boolean', default: false },
            { key: 'include_zero_stock', label: 'Include items with zero stock', type: 'boolean', default: false }
          ]
        },
        {
          id: 'stock-adjustment',
          name: 'Stock Adjustment Report',
          description: 'Upload a Sage stock file and generate adjustments to sync Sage with actual stock levels.',
          category: 'Inventory',
          type: 'upload',
          options: [{ key: 'only_differences', label: 'Only include items with differences', type: 'boolean', default: true }]
        },
        {
          id: 'inventory-change',
          name: 'Inventory Change Report',
          description: 'Track stock movements from work orders within a time window. Shows items produced, consumed, and before/after stock levels.',
          category: 'Inventory',
          type: 'time-window',
          options: []
        },
        {
          id: 'stock-movements',
          name: 'Stock Movements (Sage Format)',
          description: 'Export raw-material stock transactions between two dates in the Sage 50 Stock Transactions FULL template format.',
          category: 'Inventory',
          type: 'date-range',
          options: []
        },
        {
          id: 'assemblies',
          name: 'Sub-Assemblies & Finished Goods',
          description: 'Export two sheets derived from active BOMs: sub-assemblies (items that have children and at least one parent) and finished goods (items that have children but no parent). Each sheet shows current stock on hand and frequency of use.',
          category: 'Inventory',
          type: 'download-only',
          options: [{ key: 'include_inactive', label: 'Include inactive items', type: 'boolean', default: false }]
        },
        {
          id: 'stock-take-import',
          name: 'Stock Take Import',
          description: 'Upload a completed Stock Take xlsx. Reads every sheet in the workbook and applies the counted quantities to GCS, replaying every real stock transaction that happened after each count timestamp. You can supply a stock take date to use when the file\'s Date column is blank or broken.',
          category: 'Inventory',
          type: 'upload-apply',
          options: []
        },
        {
          id: 'inventory-value',
          name: 'Inventory Value on Date',
          description: "Total value of stock at the end of a chosen date. Stock is reconstructed from the transaction ledger and valued at each item's Sage last cost price (standard cost when no last cost is set).",
          category: 'Inventory',
          type: 'single-date',
          options: []
        },
        {
          id: 'stock-take-transactions-since',
          name: 'Transactions Since Stock Take',
          description: "Per-item list of every real stock transaction logged after a stock take date — the post-count movements a Stock Take Import folds into each item's new figure.",
          category: 'Inventory',
          type: 'stocktake-since',
          options: []
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (report) => {
    setSelectedReport(report);
    setPreviewOpen(true);
    setPreviewLoading(true);
    
    try {
      const response = await manufacturingAPI.reports.stockPreview({
        include_zero: reportOptions.include_zero_stock,
        only_counted: reportOptions.only_counted,
        include_raw_materials: reportOptions.include_raw_materials,
        include_subassemblies: reportOptions.include_subassemblies,
        include_finished: reportOptions.include_finished,
        always_include_codes: alwaysIncludeCodesFor('stock-report'),
      });
      setPreviewData(response.data);
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load report preview');
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (report) => {
    setDownloading(report.id);
    
    try {
      const response = await manufacturingAPI.reports.downloadStock({
        include_zero_stock: reportOptions.include_zero_stock,
        only_counted: reportOptions.only_counted,
        include_raw_materials: reportOptions.include_raw_materials,
        include_subassemblies: reportOptions.include_subassemblies,
        include_finished: reportOptions.include_finished,
        always_include_codes: alwaysIncludeCodesFor('stock-report'),
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Stock_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAssemblies = async (report) => {
    setDownloading(report.id);
    try {
      const response = await manufacturingAPI.reports.downloadAssemblies({
        include_inactive: reportOptions.include_inactive ? 'true' : 'false',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Assemblies_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Assemblies report downloaded');
    } catch (error) {
      console.error('Failed to download assemblies report:', error);
      toast.error('Failed to download assemblies report');
    } finally {
      setDownloading(null);
    }
  };

  const handleFileSelect = (event, report) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      setSelectedReport(report);
      handleAdjustmentPreview(file);
    }
    event.target.value = '';
  };

  const handleAdjustmentPreview = async (file) => {
    setAdjustmentDialogOpen(true);
    setPreviewLoading(true);
    setAdjustmentPreview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await manufacturingAPI.reports.stockAdjustmentPreview(formData);
      setAdjustmentPreview(response.data);
    } catch (error) {
      console.error('Failed to preview adjustment:', error);
      toast.error(error.response?.data?.error || 'Failed to process file');
      setAdjustmentPreview({ error: error.response?.data?.error || 'Failed to process file' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadAdjustment = async () => {
    if (!uploadedFile) return;
    
    setDownloading('stock-adjustment');
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('only_differences', reportOptions.only_differences);
      
      const response = await manufacturingAPI.reports.downloadStockAdjustment(formData);
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Stock_Adjustment_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Adjustment report downloaded successfully');
      setAdjustmentDialogOpen(false);
      setUploadedFile(null);
      setAdjustmentPreview(null);
    } catch (error) {
      console.error('Failed to download adjustment report:', error);
      toast.error('Failed to generate adjustment report');
    } finally {
      setDownloading(null);
    }
  };

  // Inventory Change Report handlers
  const handleOpenInventoryChangeDialog = (report) => {
    setSelectedReport(report);
    setInventoryChangeDialogOpen(true);
    setInventoryChangePreview(null);
    setInventoryChangeDate(new Date());
    setInventoryChangeStartTime(null);
    setInventoryChangeEndTime(null);
    setInventoryChangeErrors({});
  };

  const validateInventoryChangeInputs = () => {
    const errors = {};
    
    if (!inventoryChangeDate) {
      errors.date = 'Date is required';
    }
    if (!inventoryChangeStartTime) {
      errors.startTime = 'Start time is required';
    }
    if (!inventoryChangeEndTime) {
      errors.endTime = 'End time is required';
    }
    if (inventoryChangeStartTime && inventoryChangeEndTime) {
      if (isAfter(inventoryChangeStartTime, inventoryChangeEndTime) || isEqual(inventoryChangeStartTime, inventoryChangeEndTime)) {
        errors.endTime = 'End time must be after start time';
      }
    }
    
    setInventoryChangeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInventoryChangePreview = async () => {
    if (!validateInventoryChangeInputs()) {
      return;
    }
    
    setPreviewLoading(true);
    setInventoryChangePreview(null);
    
    try {
      const response = await manufacturingAPI.reports.inventoryChangePreview({
        date: format(inventoryChangeDate, 'yyyy-MM-dd'),
        start_time: format(inventoryChangeStartTime, 'HH:mm'),
        end_time: format(inventoryChangeEndTime, 'HH:mm')
      });
      setInventoryChangePreview(response.data);
    } catch (error) {
      console.error('Failed to preview inventory change:', error);
      toast.error(error.response?.data?.error || 'Failed to load preview');
      setInventoryChangePreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadInventoryChange = async () => {
    if (!validateInventoryChangeInputs()) {
      return;
    }
    
    setDownloading('inventory-change');
    
    try {
      const response = await manufacturingAPI.reports.downloadInventoryChange({
        date: format(inventoryChangeDate, 'yyyy-MM-dd'),
        start_time: format(inventoryChangeStartTime, 'HH:mm'),
        end_time: format(inventoryChangeEndTime, 'HH:mm')
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = format(inventoryChangeDate, 'yyyyMMdd');
      const startStr = format(inventoryChangeStartTime, 'HHmm');
      const endStr = format(inventoryChangeEndTime, 'HHmm');
      link.setAttribute('download', `Inventory_Change_Report_${dateStr}_${startStr}-${endStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Inventory change report downloaded successfully');
    } catch (error) {
      console.error('Failed to download inventory change report:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  // --- Stock Movements (date-range) handlers ---
  const handleOpenStockMovementsDialog = (report) => {
    setSelectedReport(report);
    setStockMovementsDialogOpen(true);
    setStockMovementsPreview(null);
    setStockMovementsStartDate(null);
    setStockMovementsEndDate(null);
    setStockMovementsErrors({});
  };

  const validateStockMovementsInputs = () => {
    const errors = {};
    if (!stockMovementsStartDate) errors.startDate = 'Start date is required';
    if (!stockMovementsEndDate) errors.endDate = 'End date is required';
    if (stockMovementsStartDate && stockMovementsEndDate &&
        isAfter(stockMovementsStartDate, stockMovementsEndDate)) {
      errors.endDate = 'End date must be on or after start date';
    }
    setStockMovementsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleStockMovementsPreview = async () => {
    if (!validateStockMovementsInputs()) return;
    setPreviewLoading(true);
    setStockMovementsPreview(null);
    try {
      const response = await manufacturingAPI.reports.stockMovementsPreview({
        start_date: format(stockMovementsStartDate, 'yyyy-MM-dd'),
        end_date: format(stockMovementsEndDate, 'yyyy-MM-dd'),
        always_include_codes: alwaysIncludeCodesFor('stock-movements'),
      });
      setStockMovementsPreview(response.data);
    } catch (error) {
      console.error('Failed to preview stock movements:', error);
      toast.error(error.response?.data?.error || 'Failed to load preview');
      setStockMovementsPreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadStockMovements = async () => {
    if (!validateStockMovementsInputs()) return;
    setDownloading('stock-movements');
    try {
      const response = await manufacturingAPI.reports.downloadStockMovements({
        start_date: format(stockMovementsStartDate, 'yyyy-MM-dd'),
        end_date: format(stockMovementsEndDate, 'yyyy-MM-dd'),
        always_include_codes: alwaysIncludeCodesFor('stock-movements'),
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const startStr = format(stockMovementsStartDate, 'yyyyMMdd');
      const endStr = format(stockMovementsEndDate, 'yyyyMMdd');
      link.setAttribute('download', `Stock_Movements_${startStr}_to_${endStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Stock movements report downloaded successfully');
    } catch (error) {
      console.error('Failed to download stock movements report:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  // --- Stock Take Import (upload-apply) handlers ---
  const resetStockTakeImport = () => {
    setStockTakeImportDialogOpen(false);
    setStockTakeImportFile(null);
    setStockTakeImportPreview(null);
    setStockTakeImportCountDate(null);
    setStockTakeImportAvailableSheets([]);
    setStockTakeImportSelectedSheets([]);
    setStockTakeImportExcludedCodes([]);
    setStockTakeImportSkipCreateCodes([]);
  };

  // Dry-run the undo of the last applied stock take, then open the confirmation dialog.
  const handleOpenUndoDialog = async () => {
    setUndoing(true);
    try {
      const { data } = await manufacturingAPI.reports.stockTakeUndoLastPreview();
      setUndoPreview(data);
      setUndoDialogOpen(true);
    } catch (error) {
      console.error('Failed to preview stock take undo:', error);
      toast.info(error.response?.data?.error || 'No stock take available to undo.');
    } finally {
      setUndoing(false);
    }
  };

  const resetUndoDialog = () => {
    setUndoDialogOpen(false);
    setUndoPreview(null);
  };

  // Execute the undo of the last applied stock take.
  const handleConfirmUndo = async () => {
    setUndoing(true);
    try {
      const { data } = await manufacturingAPI.reports.stockTakeUndoLast();
      const reversed = data.reversed ?? 0;
      toast.success(`Stock take undone — ${reversed} adjustment${reversed === 1 ? '' : 's'} reversed.`);
      resetUndoDialog();
    } catch (error) {
      console.error('Failed to undo stock take:', error);
      toast.error(error.response?.data?.error || 'Failed to undo stock take');
    } finally {
      setUndoing(false);
    }
  };

  // Runs the preview for a given file + optional count-date override + selected sheets.
  // `selectedSheets` is the set the user wants imported. `autoFillSelection` is set only on
  // the first preview for a freshly-chosen file: once the response comes back we initialise
  // the selection to every available sheet. On subsequent previews the caller's selection is
  // preserved as-is. A non-empty selection is sent to the backend; empty omits `sheets`.
  const runStockTakeImportPreview = async (file, countDate, selectedSheets = [], autoFillSelection = false) => {
    if (!file) return;
    setStockTakeImportPreview(null);
    setPreviewLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (countDate) {
        formData.append('count_date', format(countDate, 'yyyy-MM-dd'));
      }
      // Only send `sheets` when the user has explicitly narrowed to a subset.
      if (Array.isArray(selectedSheets) && selectedSheets.length > 0) {
        formData.append('sheets', JSON.stringify(selectedSheets));
      }
      const response = await manufacturingAPI.reports.stockTakeImportPreview(formData);
      const body = response.data;
      // Track which sheets the workbook offers.
      const available = body?.sheets?.available || [];
      setStockTakeImportAvailableSheets(available);
      // Auto-fill the selection only on the first preview of a fresh file.
      if (autoFillSelection && available.length > 0) {
        setStockTakeImportSelectedSheets(available.map((s) => s.name));
      }
      setStockTakeImportPreview(body);
    } catch (error) {
      console.error('Failed to preview stock take import:', error);
      const data = error.response?.data;
      // Preview endpoint returns 400 with a structured body for file errors.
      if (data && (data.file_errors || data.errors)) {
        setStockTakeImportPreview(data);
      } else {
        toast.error(error.response?.data?.error || 'Failed to process file');
        setStockTakeImportPreview({ error: error.response?.data?.error || 'Failed to process file' });
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleStockTakeImportFileSelect = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    // Fresh file: clear any prior sheet selection so the next preview auto-fills it.
    setStockTakeImportFile(file);
    setStockTakeImportAvailableSheets([]);
    setStockTakeImportSelectedSheets([]);
    // A fresh file starts with every positive adjustment included.
    setStockTakeImportExcludedCodes([]);
    setStockTakeImportSkipCreateCodes([]);
    setStockTakeImportDialogOpen(true);
    await runStockTakeImportPreview(file, stockTakeImportCountDate, [], true);
  };

  // Re-run the preview when the user changes the date and a file is already selected.
  // Preserve the current sheet selection across the re-run.
  const handleStockTakeImportCountDateChange = (newValue) => {
    setStockTakeImportCountDate(newValue);
    if (stockTakeImportFile) {
      runStockTakeImportPreview(stockTakeImportFile, newValue, stockTakeImportSelectedSheets);
    }
  };

  // Toggle a single sheet in/out of the selection and re-run the preview with the new set.
  const handleStockTakeImportSheetToggle = (sheetName) => {
    if (!stockTakeImportFile) return;
    const isSelected = stockTakeImportSelectedSheets.includes(sheetName);
    const next = isSelected
      ? stockTakeImportSelectedSheets.filter((n) => n !== sheetName)
      : [...stockTakeImportSelectedSheets, sheetName];
    setStockTakeImportSelectedSheets(next);
    // Nothing to preview with zero sheets; leave the prior preview in place and let the
    // apply guard / inline hint prompt the user to re-select at least one.
    if (next.length === 0) {
      setStockTakeImportPreview(null);
      return;
    }
    runStockTakeImportPreview(stockTakeImportFile, stockTakeImportCountDate, next, false);
  };

  // Select-all / select-none affordance for sheets.
  const handleStockTakeImportSelectAllSheets = (selectAll) => {
    if (!stockTakeImportFile) return;
    const next = selectAll ? stockTakeImportAvailableSheets.map((s) => s.name) : [];
    setStockTakeImportSelectedSheets(next);
    if (next.length === 0) {
      setStockTakeImportPreview(null);
      return;
    }
    runStockTakeImportPreview(stockTakeImportFile, stockTakeImportCountDate, next, false);
  };

  // Toggle a single positive adjustment in/out of the exclude list. Purely client-side —
  // checked means INCLUDE, so an item in `stockTakeImportExcludedCodes` is UNticked.
  const handleStockTakeImportToggleExclude = (itemCode) => {
    setStockTakeImportExcludedCodes((prev) =>
      prev.includes(itemCode) ? prev.filter((c) => c !== itemCode) : [...prev, itemCode]
    );
  };

  const handleApplyStockTakeImport = async () => {
    if (!stockTakeImportFile) return;
    // Guard: must keep at least one sheet selected.
    if (stockTakeImportAvailableSheets.length > 0 && stockTakeImportSelectedSheets.length === 0) {
      toast.error('Select at least one sheet');
      return;
    }
    setDownloading('stock-take-import');
    try {
      const formData = new FormData();
      formData.append('file', stockTakeImportFile);
      if (stockTakeImportCountDate) {
        formData.append('count_date', format(stockTakeImportCountDate, 'yyyy-MM-dd'));
      }
      // Send `sheets` only when a non-full subset is selected (backend defaults to all).
      const isFullSelection =
        stockTakeImportAvailableSheets.length > 0 &&
        stockTakeImportSelectedSheets.length === stockTakeImportAvailableSheets.length;
      if (stockTakeImportSelectedSheets.length > 0 && !isFullSelection) {
        formData.append('sheets', JSON.stringify(stockTakeImportSelectedSheets));
      }
      // Send the list of positive-adjustment item codes the user has chosen to skip.
      // Omit entirely when nothing is excluded so default behaviour is unchanged.
      if (stockTakeImportExcludedCodes.length > 0) {
        formData.append('exclude_codes', JSON.stringify(stockTakeImportExcludedCodes));
      }
      const response = await manufacturingAPI.reports.applyStockTakeImport(formData);
      const { applied, skipped_zero_delta, skipped_excluded } = response.data;
      toast.success(
        `Stock take applied: ${applied} adjustment${applied === 1 ? '' : 's'} written` +
        (skipped_zero_delta ? `, ${skipped_zero_delta} row${skipped_zero_delta === 1 ? '' : 's'} unchanged` : '') +
        (skipped_excluded ? `, ${skipped_excluded} increase${skipped_excluded === 1 ? '' : 's'} skipped` : '') +
        '.'
      );
      resetStockTakeImport();
    } catch (error) {
      console.error('Failed to apply stock take import:', error);
      const data = error.response?.data;
      if (data && (data.file_errors || data.errors)) {
        setStockTakeImportPreview(data);
        toast.error('Apply blocked - see errors in the dialog.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to apply stock take');
      }
    } finally {
      setDownloading(null);
    }
  };

  // Download the unknown (non-existent) stock codes from the current file as xlsx.
  const handleDownloadUnknownCodes = async () => {
    if (!stockTakeImportFile) return;
    setDownloading('stock-take-unknown');
    try {
      const formData = new FormData();
      formData.append('file', stockTakeImportFile);
      if (stockTakeImportCountDate) {
        formData.append('count_date', format(stockTakeImportCountDate, 'yyyy-MM-dd'));
      }
      const isFullSelection =
        stockTakeImportAvailableSheets.length > 0 &&
        stockTakeImportSelectedSheets.length === stockTakeImportAvailableSheets.length;
      if (stockTakeImportSelectedSheets.length > 0 && !isFullSelection) {
        formData.append('sheets', JSON.stringify(stockTakeImportSelectedSheets));
      }
      const response = await manufacturingAPI.reports.exportStockTakeUnknownCodes(formData);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Unknown_Stock_Codes.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Unknown codes exported.');
    } catch (error) {
      console.error('Failed to export unknown codes:', error);
      toast.error(error.response?.data?.error || 'Failed to export unknown codes');
    } finally {
      setDownloading(null);
    }
  };

  // Toggle whether an unknown code will be created (checked = create).
  const handleStockTakeImportToggleCreate = (itemCode) => {
    setStockTakeImportSkipCreateCodes((prev) =>
      prev.includes(itemCode) ? prev.filter((c) => c !== itemCode) : [...prev, itemCode]
    );
  };

  // Create the selected unknown codes as new items (stock set to counted qty).
  const handleCreateMissingItems = async () => {
    if (!stockTakeImportFile) return;
    const unknown = stockTakeImportPreview?.details?.unknown_codes || [];
    const toCreate = unknown
      .map((u) => u.stock_code)
      .filter((code) => !stockTakeImportSkipCreateCodes.includes(code));
    if (toCreate.length === 0) {
      toast.error('Select at least one code to create');
      return;
    }
    setDownloading('stock-take-create');
    try {
      const formData = new FormData();
      formData.append('file', stockTakeImportFile);
      formData.append('create_codes', JSON.stringify(toCreate));
      formData.append('item_type', stockTakeImportCreateType);
      if (stockTakeImportCountDate) {
        formData.append('count_date', format(stockTakeImportCountDate, 'yyyy-MM-dd'));
      }
      const isFullSelection =
        stockTakeImportAvailableSheets.length > 0 &&
        stockTakeImportSelectedSheets.length === stockTakeImportAvailableSheets.length;
      if (stockTakeImportSelectedSheets.length > 0 && !isFullSelection) {
        formData.append('sheets', JSON.stringify(stockTakeImportSelectedSheets));
      }
      const { data } = await manufacturingAPI.reports.createStockTakeMissingItems(formData);
      const createdN = data.created?.length || 0;
      const skippedN = data.skipped_existing?.length || 0;
      const errN = data.errors?.length || 0;
      toast.success(
        `Created ${createdN} item${createdN === 1 ? '' : 's'}` +
        (skippedN ? `, ${skippedN} already existed` : '') +
        (errN ? `, ${errN} failed` : '') + '.'
      );
      // Re-run the preview so the created codes drop off the unknown list.
      setStockTakeImportSkipCreateCodes([]);
      runStockTakeImportPreview(
        stockTakeImportFile, stockTakeImportCountDate, stockTakeImportSelectedSheets, false,
      );
    } catch (error) {
      console.error('Failed to create missing items:', error);
      toast.error(error.response?.data?.error || 'Failed to create items');
    } finally {
      setDownloading(null);
    }
  };

  // --- Transactions Since Stock Take (stocktake-since) handlers ---
  const handleOpenStockTakeSinceDialog = () => {
    setStockTakeSinceDialogOpen(true);
    setStockTakeSincePreview(null);
    setStockTakeSinceStartDate(null);
    setStockTakeSinceEndDate(null);
    setStockTakeSinceErrors({});
  };

  const validateStockTakeSinceInputs = () => {
    const errors = {};
    if (!stockTakeSinceStartDate) errors.startDate = 'Stock take date is required';
    if (stockTakeSinceStartDate && stockTakeSinceEndDate &&
        isAfter(stockTakeSinceStartDate, stockTakeSinceEndDate)) {
      errors.endDate = 'End date must be on or after the stock take date';
    }
    setStockTakeSinceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildStockTakeSinceParams = () => {
    const params = { start_date: format(stockTakeSinceStartDate, 'yyyy-MM-dd') };
    if (stockTakeSinceEndDate) {
      params.end_date = format(stockTakeSinceEndDate, 'yyyy-MM-dd');
    }
    return params;
  };

  const handleStockTakeSincePreview = async () => {
    if (!validateStockTakeSinceInputs()) return;
    setPreviewLoading(true);
    setStockTakeSincePreview(null);
    try {
      const response = await manufacturingAPI.reports.stockTakeTransactionsSincePreview(buildStockTakeSinceParams());
      setStockTakeSincePreview(response.data);
    } catch (error) {
      console.error('Failed to preview transactions since stock take:', error);
      toast.error(error.response?.data?.error || 'Failed to load preview');
      setStockTakeSincePreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadStockTakeSince = async () => {
    if (!validateStockTakeSinceInputs()) return;
    setDownloading('stock-take-transactions-since');
    try {
      const response = await manufacturingAPI.reports.downloadStockTakeTransactionsSince(buildStockTakeSinceParams());
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const startStr = format(stockTakeSinceStartDate, 'yyyyMMdd');
      link.setAttribute('download', `Transactions_Since_StockTake_${startStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Transactions since stock take downloaded successfully');
    } catch (error) {
      console.error('Failed to download transactions since stock take:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  // --- BOM Cost Roll-Up (bom-search) handlers ---
  useEffect(() => {
    if (!bomCostDialogOpen) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setBomCostSearchLoading(true);
      try {
        const response = await manufacturingAPI.reports.bomCostSearch({ q: bomCostQuery });
        if (!cancelled) setBomCostOptions(response.data.results || []);
      } catch (error) {
        console.error('BOM search failed:', error);
        if (!cancelled) setBomCostOptions([]);
      } finally {
        if (!cancelled) setBomCostSearchLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bomCostQuery, bomCostDialogOpen]);

  const handleOpenBomCostDialog = () => {
    setBomCostDialogOpen(true);
    setBomCostQuery('');
    setBomCostSelected(null);
    setBomCostQuantity('1');
    setBomCostPreview(null);
  };

  const bomCostQuantityValid = !!bomCostQuantity && !isNaN(Number(bomCostQuantity)) && Number(bomCostQuantity) > 0;

  const handleBomCostPreview = async () => {
    if (!bomCostSelected || !bomCostQuantityValid) return;
    setPreviewLoading(true);
    setBomCostPreview(null);
    try {
      const response = await manufacturingAPI.reports.bomCostPreview({
        item_id: bomCostSelected.id,
        quantity: bomCostQuantity
      });
      setBomCostPreview(response.data);
    } catch (error) {
      console.error('Failed to preview BOM cost roll-up:', error);
      toast.error(error.response?.data?.error || 'Failed to load preview');
      setBomCostPreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadBomCost = async () => {
    if (!bomCostSelected || !bomCostQuantityValid) return;
    setDownloading('bom-cost');
    try {
      const response = await manufacturingAPI.reports.downloadBomCost({
        item_id: bomCostSelected.id,
        quantity: bomCostQuantity
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOM_Cost_${bomCostSelected.item_code}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('BOM cost report downloaded successfully');
    } catch (error) {
      console.error('Failed to download BOM cost report:', error);
      let message = 'Failed to generate report';
      if (error.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await error.response.data.text()).error || message;
        } catch (_) { /* keep default */ }
      } else if (error.response?.data?.error) {
        message = error.response.data.error;
      }
      toast.error(message);
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadBomCostAll = async (monthDate, downloadKey = 'bom-cost-all') => {
    setDownloading(downloadKey);
    try {
      const params = { only_ordered: bomCostAllOnlyOrdered };
      if (monthDate) params.month = format(monthDate, 'yyyy-MM');
      const response = await manufacturingAPI.reports.downloadBomCostAllTopLevel(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stem = monthDate
        ? `Top_Level_BOMs_${format(monthDate, 'yyyy-MM')}`
        : `Top_Level_BOMs_${format(new Date(), 'yyyyMMdd')}`;
      link.setAttribute('download', `${stem}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Top-level BOMs report downloaded successfully');
    } catch (error) {
      console.error('Failed to download top-level BOMs report:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  // --- BOM Usage by Month (bom-usage) handlers ---
  const handleOpenBomUsageDialog = () => {
    setBomUsageDialogOpen(true);
    setBomUsageMonth(new Date());
    setBomUsagePreview(null);
  };

  const handleBomUsagePreview = async () => {
    if (!bomUsageMonth) return;
    setPreviewLoading(true);
    setBomUsagePreview(null);
    try {
      const response = await manufacturingAPI.reports.bomUsagePreview({
        month: format(bomUsageMonth, 'yyyy-MM'),
        always_include_codes: alwaysIncludeCodesFor('bom-usage'),
      });
      setBomUsagePreview(response.data);
    } catch (error) {
      console.error('Failed to preview BOM usage:', error);
      setBomUsagePreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadBomUsage = async () => {
    if (!bomUsageMonth) return;
    setDownloading('bom-usage');
    try {
      const response = await manufacturingAPI.reports.downloadBomUsage({
        month: format(bomUsageMonth, 'yyyy-MM'),
        always_include_codes: alwaysIncludeCodesFor('bom-usage'),
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BOM_Usage_${format(bomUsageMonth, 'yyyy-MM')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('BOM usage report downloaded successfully');
    } catch (error) {
      console.error('Failed to download BOM usage report:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  // --- Raw Material Usage (raw-material-usage) handlers ---
  const handleOpenRawMaterialUsageDialog = () => {
    setRawMaterialUsageDialogOpen(true);
    setRawMaterialUsageStartDate(new Date(new Date().getFullYear(), 0, 1));
    setRawMaterialUsageEndDate(new Date());
    setRawMaterialUsagePreview(null);
  };

  const handleRawMaterialUsagePreview = async () => {
    if (!rawMaterialUsageStartDate || !rawMaterialUsageEndDate) return;
    setPreviewLoading(true);
    setRawMaterialUsagePreview(null);
    try {
      const response = await manufacturingAPI.reports.rawMaterialUsagePreview({
        start_date: format(rawMaterialUsageStartDate, 'yyyy-MM-dd'),
        end_date: format(rawMaterialUsageEndDate, 'yyyy-MM-dd'),
        always_include_codes: alwaysIncludeCodesFor('raw-material-usage'),
      });
      setRawMaterialUsagePreview(response.data);
    } catch (error) {
      console.error('Failed to preview raw material usage:', error);
      setRawMaterialUsagePreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadRawMaterialUsage = async () => {
    if (!rawMaterialUsageStartDate || !rawMaterialUsageEndDate) return;
    setDownloading('raw-material-usage');
    try {
      const response = await manufacturingAPI.reports.downloadRawMaterialUsage({
        start_date: format(rawMaterialUsageStartDate, 'yyyy-MM-dd'),
        end_date: format(rawMaterialUsageEndDate, 'yyyy-MM-dd'),
        always_include_codes: alwaysIncludeCodesFor('raw-material-usage'),
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Raw_Material_Usage_${format(rawMaterialUsageStartDate, 'yyyyMMdd')}_to_${format(rawMaterialUsageEndDate, 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Raw material usage report downloaded successfully');
    } catch (error) {
      console.error('Failed to download raw material usage report:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  // --- Inventory Value on Date (single-date) handlers ---
  const handleOpenInventoryValueDialog = () => {
    setInventoryValueDialogOpen(true);
    setInventoryValuePreview(null);
    setInventoryValueDate(null);
    setInventoryValueErrors({});
  };

  const validateInventoryValueInputs = () => {
    const errors = {};
    if (!inventoryValueDate) errors.date = 'Date is required';
    setInventoryValueErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInventoryValuePreview = async () => {
    if (!validateInventoryValueInputs()) return;
    setPreviewLoading(true);
    setInventoryValuePreview(null);
    try {
      const response = await manufacturingAPI.reports.inventoryValuePreview({
        date: format(inventoryValueDate, 'yyyy-MM-dd'),
        only_counted: inventoryValueOnlyCounted,
        always_include_codes: alwaysIncludeCodesFor('inventory-value'),
      });
      setInventoryValuePreview(response.data);
    } catch (error) {
      console.error('Failed to preview inventory value:', error);
      toast.error(error.response?.data?.error || 'Failed to load preview');
      setInventoryValuePreview({ error: error.response?.data?.error || 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadInventoryValue = async () => {
    if (!validateInventoryValueInputs()) return;
    setDownloading('inventory-value');
    try {
      const response = await manufacturingAPI.reports.downloadInventoryValue({
        date: format(inventoryValueDate, 'yyyy-MM-dd'),
        only_counted: inventoryValueOnlyCounted,
        always_include_codes: alwaysIncludeCodesFor('inventory-value'),
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Inventory_Value_${format(inventoryValueDate, 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Inventory value report downloaded successfully');
    } catch (error) {
      console.error('Failed to download inventory value report:', error);
      toast.error(error.response?.data?.error || 'Failed to generate report');
    } finally {
      setDownloading(null);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Inventory': return <Inventory />;
      case 'Production': return <Assessment />;
      default: return <TableChart />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Inventory': return 'primary';
      case 'Production': return 'success';
      case 'Sales': return 'warning';
      default: return 'default';
    }
  };

  const renderReportCard = (report) => {
    const isUploadType = report.type === 'upload';
    const isUploadApplyType = report.type === 'upload-apply';
    const isTimeWindowType = report.type === 'time-window';
    const isDateRangeType = report.type === 'date-range';
    const isDownloadOnlyType = report.type === 'download-only';
    const isStockTakeSinceType = report.type === 'stocktake-since';
    const isSingleDateType = report.type === 'single-date';
    const isBomSearchType = report.type === 'bom-search';
    const isBomUsageType = report.type === 'bom-usage';
    const isRawMaterialUsageType = report.type === 'raw-material-usage';

    return (
      <Grid item xs={12} md={6} lg={4} key={report.id}>
        <Card 
          elevation={2}
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
          }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: (isUploadType || isUploadApplyType) ? 'warning.lighter' : (isTimeWindowType || isDateRangeType || isStockTakeSinceType || isSingleDateType || isBomSearchType || isBomUsageType || isRawMaterialUsageType) ? 'info.lighter' : 'primary.lighter',
                  color: (isUploadType || isUploadApplyType) ? 'warning.main' : (isTimeWindowType || isDateRangeType || isStockTakeSinceType || isSingleDateType || isBomSearchType || isBomUsageType || isRawMaterialUsageType) ? 'info.main' : 'primary.main',
                  mr: 2
                }}
              >
                {(isUploadType || isUploadApplyType) ? <CompareArrows /> : isTimeWindowType ? <AccessTime /> : (isDateRangeType || isStockTakeSinceType || isSingleDateType || isBomUsageType || isRawMaterialUsageType) ? <CalendarMonth /> : isBomSearchType ? <AccountTree /> : getCategoryIcon(report.category)}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {report.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip label={report.category} size="small" color={getCategoryColor(report.category)} />
                  {isUploadType && (
                    <Chip label="Upload Required" size="small" color="warning" variant="outlined" />
                  )}
                  {isUploadApplyType && (
                    <Chip label="Upload & Apply" size="small" color="warning" variant="outlined" />
                  )}
                  {isTimeWindowType && (
                    <Chip label="Time Window" size="small" color="info" variant="outlined" />
                  )}
                  {isDateRangeType && (
                    <Chip label="Date Range" size="small" color="info" variant="outlined" />
                  )}
                  {isStockTakeSinceType && (
                    <Chip label="Date Range" size="small" color="info" variant="outlined" />
                  )}
                  {isSingleDateType && (
                    <Chip label="Single Date" size="small" color="info" variant="outlined" />
                  )}
                  {isBomSearchType && (
                    <Chip label="BOM Search" size="small" color="info" variant="outlined" />
                  )}
                  {isBomUsageType && (
                    <Chip label="Monthly" size="small" color="info" variant="outlined" />
                  )}
                  {isRawMaterialUsageType && (
                    <Chip label="Date Range" size="small" color="info" variant="outlined" />
                  )}
                </Box>
              </Box>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {report.description}
            </Typography>

            {report.options && report.options.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Options
                </Typography>
                {report.options.map((option) => (
                  <FormControlLabel
                    key={option.key}
                    control={
                      <Checkbox
                        checked={reportOptions[option.key] ?? option.default}
                        onChange={(e) => setReportOptions({
                          ...reportOptions,
                          [option.key]: e.target.checked
                        })}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{option.label}</Typography>}
                  />
                ))}
              </Box>
            )}

            {ALWAYS_INCLUDE_REPORTS.includes(report.id) && alwaysIncludeField(report.id, true)}

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Chip label="XLSX" size="small" variant="outlined" />
              {isTimeWindowType && (
                <Chip label="3 Sheets" size="small" variant="outlined" color="info" />
              )}
            </Box>
          </CardContent>
          
          <Divider />
          
          <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
            {isUploadType ? (
              <>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={(e) => handleFileSelect(e, report)}
                />
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<CloudUpload />}
                  onClick={() => fileInputRef.current?.click()}
                  fullWidth
                >
                  Upload Sage File
                </Button>
              </>
            ) : isUploadApplyType ? (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <input
                  type="file"
                  accept=".xlsx"
                  style={{ display: 'none' }}
                  ref={stockTakeImportFileInputRef}
                  onChange={handleStockTakeImportFileSelect}
                  data-testid="stock-take-import-file-input"
                />
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<CloudUpload />}
                  onClick={() => stockTakeImportFileInputRef.current?.click()}
                  fullWidth
                  data-testid="stock-take-import-upload-btn"
                >
                  Upload Stock Take
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={undoing && !undoDialogOpen ? <CircularProgress size={16} color="inherit" /> : <Restore />}
                  onClick={handleOpenUndoDialog}
                  disabled={undoing}
                  fullWidth
                  data-testid="stock-take-undo-btn"
                >
                  Undo last stock take
                </Button>
              </Box>
            ) : isTimeWindowType ? (
              <Button
                variant="contained"
                color="info"
                startIcon={<CalendarMonth />}
                onClick={() => handleOpenInventoryChangeDialog(report)}
                fullWidth
              >
                Configure & Generate
              </Button>
            ) : isDateRangeType ? (
              <Button
                variant="contained"
                color="info"
                startIcon={<CalendarMonth />}
                onClick={() => handleOpenStockMovementsDialog(report)}
                fullWidth
              >
                Configure & Generate
              </Button>
            ) : isStockTakeSinceType ? (
              <Button
                variant="contained"
                color="info"
                startIcon={<CalendarMonth />}
                onClick={() => handleOpenStockTakeSinceDialog(report)}
                fullWidth
              >
                Configure & Generate
              </Button>
            ) : isSingleDateType ? (
              <Button
                variant="contained"
                color="info"
                startIcon={<CalendarMonth />}
                onClick={() => handleOpenInventoryValueDialog(report)}
                fullWidth
              >
                Configure & Generate
              </Button>
            ) : isBomSearchType ? (
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<Search />}
                  onClick={handleOpenBomCostDialog}
                  fullWidth
                >
                  Search BOM & Generate
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={downloading === 'bom-cost-all' ? <CircularProgress size={16} color="inherit" /> : <Download />}
                  onClick={() => handleDownloadBomCostAll(bomCostAllMonth)}
                  disabled={downloading === 'bom-cost-all' || downloading === 'bom-cost-month'}
                  fullWidth
                >
                  {downloading === 'bom-cost-all' ? 'Generating...' : 'Download all top-level BOMs'}
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  startIcon={downloading === 'bom-cost-month' ? <CircularProgress size={16} color="inherit" /> : <CalendarMonth />}
                  onClick={() => handleDownloadBomCostAll(new Date(), 'bom-cost-month')}
                  disabled={downloading === 'bom-cost-all' || downloading === 'bom-cost-month'}
                  fullWidth
                >
                  {downloading === 'bom-cost-month' ? 'Generating...' : 'BOM costs for current month'}
                </Button>
                <DatePicker
                  label="Month (optional)"
                  views={['year', 'month']}
                  openTo="month"
                  value={bomCostAllMonth}
                  onChange={(newValue) => setBomCostAllMonth(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      helperText: 'Limits "Download all" to BOMs ordered in this month'
                    },
                    field: { clearable: true }
                  }}
                />
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Checkbox
                      checked={bomCostAllOnlyOrdered}
                      onChange={(e) => setBomCostAllOnlyOrdered(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Only use BOMs used in orders
                    </Typography>
                  }
                />
              </Box>
            ) : isBomUsageType ? (
              <Button
                variant="contained"
                color="info"
                startIcon={<CalendarMonth />}
                onClick={handleOpenBomUsageDialog}
                fullWidth
              >
                Configure & Generate
              </Button>
            ) : isRawMaterialUsageType ? (
              <Button
                variant="contained"
                color="info"
                startIcon={<CalendarMonth />}
                onClick={handleOpenRawMaterialUsageDialog}
                fullWidth
              >
                Configure & Generate
              </Button>
            ) : isDownloadOnlyType ? (
              <Button
                variant="contained"
                startIcon={downloading === report.id ? <CircularProgress size={20} color="inherit" /> : <Download />}
                onClick={() => handleDownloadAssemblies(report)}
                disabled={downloading === report.id}
                fullWidth
              >
                {downloading === report.id ? 'Downloading...' : 'Download'}
              </Button>
            ) : (
              <>
                <Button startIcon={<Visibility />} onClick={() => handlePreview(report)} disabled={previewLoading}>
                  Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={downloading === report.id ? <CircularProgress size={20} color="inherit" /> : <Download />}
                  onClick={() => handleDownload(report)}
                  disabled={downloading === report.id}
                >
                  {downloading === report.id ? 'Downloading...' : 'Download'}
                </Button>
              </>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  // --- Stock Take Import: rich-section render helpers ---
  // Generic scrolling table for a list of objects given column definitions.
  // columns: [{ key, label, align, render?(row), cellSx?(row) }]
  const renderStockTakeTable = (rows, columns) => (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align || 'left'}>
                <strong>{col.label}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align || 'left'} sx={col.cellSx ? col.cellSx(row) : undefined}>
                  {col.render ? col.render(row) : row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // A collapsible section with a coloured count chip in the header.
  // tone: 'error' | 'warning' | 'info'
  const renderStockTakeSection = ({ tone, icon, title, count, defaultExpanded = false, children }) => (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        mb: 1,
        border: 1,
        borderColor: `${tone}.light`,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: `${tone}.lighter` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="subtitle2">{title}</Typography>
          <Chip label={count?.toLocaleString() ?? 0} size="small" color={tone} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Reports
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate and download reports for your manufacturing data
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {reports.map((report) => renderReportCard(report))}
        </Grid>

        <CustomReportsSection />

        {reports.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Assessment sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No reports available</Typography>
          </Paper>
        )}

        {/* Stock Report Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{selectedReport?.name} - Preview</Typography>
              <IconButton onClick={() => setPreviewOpen(false)}><Close /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {previewLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : previewData ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.lighter' }}>
                      <Typography variant="h4" fontWeight={600} color="primary.main">
                        {previewData.total_items?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Items</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, bgcolor: 'success.lighter' }}>
                      <Typography variant="h4" fontWeight={600} color="success.main">
                        {previewData.total_quantity?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Quantity</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" gutterBottom>Preview (First 20 Items)</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Stock Code</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell align="right"><strong>Quantity</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.preview?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>AI</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{item.item_code}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.item_name}</Typography>
                          </TableCell>
                          <TableCell>{new Date().toLocaleDateString('en-GB')}</TableCell>
                          <TableCell align="right">{item.quantity.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {previewData.has_more && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing first 20 items. Full report contains {previewData.total_items} items.
                  </Alert>
                )}
              </Box>
            ) : (
              <Alert severity="error">Failed to load preview data</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button variant="contained" startIcon={<Download />} onClick={() => { setPreviewOpen(false); handleDownload(selectedReport); }}>
              Download Report
            </Button>
          </DialogActions>
        </Dialog>

        {/* Stock Adjustment Preview Dialog */}
        <Dialog 
          open={adjustmentDialogOpen} 
          onClose={() => { setAdjustmentDialogOpen(false); setUploadedFile(null); setAdjustmentPreview(null); }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Stock Adjustment Report</Typography>
                {uploadedFile && <Typography variant="caption" color="text.secondary">File: {uploadedFile.name}</Typography>}
              </Box>
              <IconButton onClick={() => { setAdjustmentDialogOpen(false); setUploadedFile(null); setAdjustmentPreview(null); }}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {previewLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography>Analyzing stock differences...</Typography>
              </Box>
            ) : adjustmentPreview?.error ? (
              <Alert severity="error">{adjustmentPreview.error}</Alert>
            ) : adjustmentPreview ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {adjustmentPreview.summary?.items_in_sage_file?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Items in Sage File</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {adjustmentPreview.summary?.items_in_database?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Items in Database</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'success.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp color="success" />
                        <Typography variant="h5" fontWeight={600} color="success.main">
                          {adjustmentPreview.summary?.items_to_increase || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Items to Increase (+{adjustmentPreview.summary?.total_increase_qty?.toLocaleString() || 0})
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'error.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingDown color="error" />
                        <Typography variant="h5" fontWeight={600} color="error.main">
                          {adjustmentPreview.summary?.items_to_decrease || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Items to Decrease (-{adjustmentPreview.summary?.total_decrease_qty?.toLocaleString() || 0})
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    icon={<CheckCircle />}
                    label={`${adjustmentPreview.summary?.items_matched || 0} items already match`}
                    color="success"
                    variant="outlined"
                  />
                  {adjustmentPreview.summary?.items_not_in_db > 0 && (
                    <Chip label={`${adjustmentPreview.summary.items_not_in_db} in Sage but not in DB`} color="warning" variant="outlined" />
                  )}
                  {adjustmentPreview.summary?.items_not_in_sage > 0 && (
                    <Chip label={`${adjustmentPreview.summary.items_not_in_sage} in DB but not in Sage`} color="info" variant="outlined" />
                  )}
                </Box>

                <Typography variant="subtitle2" gutterBottom>Adjustments Preview (Largest differences first)</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Stock Code</strong></TableCell>
                        <TableCell align="right"><strong>Sage Qty</strong></TableCell>
                        <TableCell align="right"><strong>Actual Qty</strong></TableCell>
                        <TableCell align="right"><strong>Adjustment</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adjustmentPreview.preview?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip label={item.type} size="small" color={item.type === 'AI' ? 'success' : 'error'} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{item.item_code}</Typography>
                            {item.item_name && <Typography variant="caption" color="text.secondary">{item.item_name}</Typography>}
                          </TableCell>
                          <TableCell align="right">{item.sage_qty.toLocaleString()}</TableCell>
                          <TableCell align="right">{item.actual_qty.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600} color={item.difference > 0 ? 'success.main' : 'error.main'}>
                              {item.difference > 0 ? '+' : ''}{item.difference.toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {adjustmentPreview.has_more && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Showing first 50 adjustments. Full report contains {adjustmentPreview.summary?.total_adjustments} adjustments.
                  </Alert>
                )}

                {adjustmentPreview.summary?.total_adjustments === 0 && (
                  <Alert severity="success" sx={{ mt: 2 }}>All stock levels match! No adjustments needed.</Alert>
                )}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAdjustmentDialogOpen(false); setUploadedFile(null); setAdjustmentPreview(null); }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={downloading === 'stock-adjustment' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadAdjustment}
              disabled={downloading === 'stock-adjustment' || !adjustmentPreview || adjustmentPreview.error || adjustmentPreview.summary?.total_adjustments === 0}
            >
              {downloading === 'stock-adjustment' ? 'Generating...' : 'Download Adjustment Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Inventory Change Report Dialog */}
        <Dialog 
          open={inventoryChangeDialogOpen} 
          onClose={() => setInventoryChangeDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Inventory Change Report</Typography>
                <Typography variant="caption" color="text.secondary">
                  Track stock movements from work orders within a time window
                </Typography>
              </Box>
              <IconButton onClick={() => setInventoryChangeDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {/* Time Window Configuration */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Select Date and Time Window
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <DatePicker
                    label="Date"
                    value={inventoryChangeDate}
                    onChange={(newValue) => setInventoryChangeDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!inventoryChangeErrors.date,
                        helperText: inventoryChangeErrors.date
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TimePicker
                    label="Start Time"
                    value={inventoryChangeStartTime}
                    onChange={(newValue) => setInventoryChangeStartTime(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!inventoryChangeErrors.startTime,
                        helperText: inventoryChangeErrors.startTime,
                        placeholder: 'Required'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TimePicker
                    label="End Time"
                    value={inventoryChangeEndTime}
                    onChange={(newValue) => setInventoryChangeEndTime(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!inventoryChangeErrors.endTime,
                        helperText: inventoryChangeErrors.endTime,
                        placeholder: 'Required'
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleInventoryChangePreview}
                  disabled={previewLoading}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <Visibility />}
                >
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Preview Results */}
            {inventoryChangePreview?.error ? (
              <Alert severity="error">{inventoryChangePreview.error}</Alert>
            ) : inventoryChangePreview ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview for {inventoryChangePreview.time_window?.date} ({inventoryChangePreview.time_window?.start} - {inventoryChangePreview.time_window?.end})
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {inventoryChangePreview.summary?.total_transactions?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {inventoryChangePreview.summary?.unique_items?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Unique Items</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'success.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp color="success" />
                        <Typography variant="h5" fontWeight={600} color="success.main">
                          {inventoryChangePreview.summary?.total_added?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">Total Added</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'error.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingDown color="error" />
                        <Typography variant="h5" fontWeight={600} color="error.main">
                          {inventoryChangePreview.summary?.total_removed?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">Total Removed</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${inventoryChangePreview.summary?.unique_work_orders || 0} Work Orders`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={`${inventoryChangePreview.summary?.unique_operations || 0} Operations`}
                    color="secondary"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Net Change: ${inventoryChangePreview.summary?.net_change >= 0 ? '+' : ''}${inventoryChangePreview.summary?.net_change?.toLocaleString() || 0}`}
                    color={inventoryChangePreview.summary?.net_change >= 0 ? 'success' : 'error'}
                  />
                </Box>

                {inventoryChangePreview.summary?.total_transactions === 0 ? (
                  <Alert severity="info">
                    No stock transactions found for the selected time window.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Recent Transactions (First 20)</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Time</strong></TableCell>
                            <TableCell><strong>Item</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell align="right"><strong>Qty Change</strong></TableCell>
                            <TableCell><strong>Work Order</strong></TableCell>
                            <TableCell><strong>Operation</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventoryChangePreview.preview?.map((txn, index) => (
                            <TableRow key={index}>
                              <TableCell>{txn.timestamp}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{txn.item_code}</Typography>
                                <Typography variant="caption" color="text.secondary">{txn.item_name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={txn.transaction_type} 
                                  size="small" 
                                  color={txn.quantity > 0 ? 'success' : 'error'} 
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography 
                                  fontWeight={600} 
                                  color={txn.quantity > 0 ? 'success.main' : 'error.main'}
                                >
                                  {txn.quantity > 0 ? '+' : ''}{txn.quantity.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>{txn.work_order}</TableCell>
                              <TableCell>{txn.operation}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    {inventoryChangePreview.has_more && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Showing first 20 transactions. Full report contains {inventoryChangePreview.summary?.total_transactions} transactions.
                      </Alert>
                    )}
                  </>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                  The downloaded Excel file will contain 3 sheets: By Item, By Operation, and Summary.
                </Alert>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AccessTime sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Select a date and time window, then click Preview to see the data.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInventoryChangeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={downloading === 'inventory-change' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadInventoryChange}
              disabled={
                downloading === 'inventory-change' ||
                !inventoryChangeStartTime ||
                !inventoryChangeEndTime ||
                (inventoryChangePreview && inventoryChangePreview.summary?.total_transactions === 0)
              }
            >
              {downloading === 'inventory-change' ? 'Generating...' : 'Download Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Stock Movements (date-range) Report Dialog */}
        <Dialog
          open={stockMovementsDialogOpen}
          onClose={() => setStockMovementsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Stock Movements (Sage Format)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Raw-material stock transactions between two dates, in the Sage 50 Stock Transactions FULL template format
                </Typography>
              </Box>
              <IconButton onClick={() => setStockMovementsDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Select Date Range (inclusive)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Start Date"
                    value={stockMovementsStartDate}
                    onChange={(newValue) => setStockMovementsStartDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!stockMovementsErrors.startDate,
                        helperText: stockMovementsErrors.startDate
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="End Date"
                    value={stockMovementsEndDate}
                    onChange={(newValue) => setStockMovementsEndDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!stockMovementsErrors.endDate,
                        helperText: stockMovementsErrors.endDate
                      }
                    }}
                  />
                </Grid>
              </Grid>
              {alwaysIncludeField('stock-movements')}
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleStockMovementsPreview}
                  disabled={previewLoading}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <Visibility />}
                >
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {stockMovementsPreview?.error ? (
              <Alert severity="error">{stockMovementsPreview.error}</Alert>
            ) : stockMovementsPreview ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preview for {stockMovementsPreview.date_range?.start} → {stockMovementsPreview.date_range?.end}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {stockMovementsPreview.summary?.total_transactions?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {stockMovementsPreview.summary?.unique_items?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Unique Raw Materials</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'success.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp color="success" />
                        <Typography variant="h5" fontWeight={600} color="success.main">
                          {stockMovementsPreview.summary?.total_in?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">Total In</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'error.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingDown color="error" />
                        <Typography variant="h5" fontWeight={600} color="error.main">
                          {stockMovementsPreview.summary?.total_out?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">Total Out</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {stockMovementsPreview.summary?.total_transactions === 0 ? (
                  <Alert severity="info">
                    No raw-material stock transactions found in this date range.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>First 20 Transactions</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Timestamp</strong></TableCell>
                            <TableCell><strong>Item</strong></TableCell>
                            <TableCell><strong>Sage Type</strong></TableCell>
                            <TableCell><strong>Source Type</strong></TableCell>
                            <TableCell align="right"><strong>Qty</strong></TableCell>
                            <TableCell><strong>Reference</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockMovementsPreview.preview?.map((txn, index) => (
                            <TableRow key={index}>
                              <TableCell>{txn.timestamp}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{txn.item_code}</Typography>
                                <Typography variant="caption" color="text.secondary">{txn.item_name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={txn.sage_type}
                                  size="small"
                                  color={txn.sage_type === 'AI' ? 'success' : 'error'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{txn.transaction_type}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight={600}
                                  color={txn.quantity > 0 ? 'success.main' : 'error.main'}
                                >
                                  {txn.quantity > 0 ? '+' : ''}{txn.quantity.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>{txn.reference}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {stockMovementsPreview.has_more && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Showing first 20 transactions. Full report contains {stockMovementsPreview.summary?.total_transactions}.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarMonth sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Pick a start and end date (inclusive), then click Preview.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStockMovementsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={downloading === 'stock-movements' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadStockMovements}
              disabled={
                downloading === 'stock-movements' ||
                !stockMovementsStartDate ||
                !stockMovementsEndDate ||
                (stockMovementsPreview && stockMovementsPreview.summary?.total_transactions === 0)
              }
            >
              {downloading === 'stock-movements' ? 'Generating...' : 'Download Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Stock Take Import (upload-apply) Dialog */}
        <Dialog
          open={stockTakeImportDialogOpen}
          onClose={resetStockTakeImport}
          maxWidth="lg"
          fullWidth
          data-testid="stock-take-import-dialog"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Stock Take Import — Apply to GCS</Typography>
                <Typography variant="caption" color="text.secondary">
                  Reads each row's Date+Time and Actual, then writes an adjustment after replaying real transactions since the count.
                </Typography>
              </Box>
              <IconButton onClick={resetStockTakeImport}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {/* Optional stock take date override */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Stock take date (use if the file's Date column is blank or broken)"
                    value={stockTakeImportCountDate}
                    onChange={handleStockTakeImportCountDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'All counted rows will be dated to this day; transactions logged after it are folded in as movements since the count.'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Sheets to import */}
            {stockTakeImportAvailableSheets.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Sheets to import ({stockTakeImportSelectedSheets.length} of {stockTakeImportAvailableSheets.length})
                  </Typography>
                  <Box>
                    <Button
                      size="small"
                      onClick={() => handleStockTakeImportSelectAllSheets(true)}
                      disabled={!stockTakeImportFile || previewLoading}
                    >
                      Select all
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleStockTakeImportSelectAllSheets(false)}
                      disabled={!stockTakeImportFile || previewLoading}
                    >
                      Select none
                    </Button>
                  </Box>
                </Box>
                <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 180, overflow: 'auto' }}>
                  <FormGroup row>
                    {stockTakeImportAvailableSheets.map((sheet) => (
                      <FormControlLabel
                        key={sheet.name}
                        sx={{ minWidth: 220 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={stockTakeImportSelectedSheets.includes(sheet.name)}
                            onChange={() => handleStockTakeImportSheetToggle(sheet.name)}
                            disabled={!stockTakeImportFile || previewLoading}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {sheet.name}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              ({sheet.row_count?.toLocaleString() ?? 0} rows)
                            </Typography>
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Paper>
                {stockTakeImportSelectedSheets.length === 0 && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                    Select at least one sheet to import.
                  </Typography>
                )}
                <Divider sx={{ mt: 2 }} />
              </Box>
            )}

            {previewLoading ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress />
                <Typography color="text.secondary" sx={{ mt: 2 }}>Analysing file...</Typography>
              </Box>
            ) : stockTakeImportPreview?.error ? (
              <Alert severity="error">{stockTakeImportPreview.error}</Alert>
            ) : (stockTakeImportPreview?.file_errors?.length && !stockTakeImportPreview?.summary) ? (
              <Alert severity="error">
                <Typography variant="subtitle2" gutterBottom>File-level errors</Typography>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {stockTakeImportPreview.file_errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </Alert>
            ) : stockTakeImportPreview ? (
              <Box>
                {stockTakeImportPreview.count_date_override && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Counts dated: {stockTakeImportPreview.count_date_override}
                  </Alert>
                )}
                {/* Summary cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {stockTakeImportPreview.summary?.rows_to_apply?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Rows to Apply</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'success.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUp color="success" />
                        <Typography variant="h5" fontWeight={600} color="success.main">
                          {stockTakeImportPreview.summary?.items_increased?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Items Increased (+{stockTakeImportPreview.summary?.total_increase?.toLocaleString() || 0})
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'error.lighter' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingDown color="error" />
                        <Typography variant="h5" fontWeight={600} color="error.main">
                          {stockTakeImportPreview.summary?.items_decreased?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Items Decreased (−{stockTakeImportPreview.summary?.total_decrease?.toLocaleString() || 0})
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {stockTakeImportPreview.summary?.items_unchanged?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Unchanged (Δ = 0)</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* File-level errors (block apply) */}
                {stockTakeImportPreview.file_errors?.length > 0 && (
                  <Alert severity="error" icon={<ErrorOutline />} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>File-level errors</Typography>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {stockTakeImportPreview.file_errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </Alert>
                )}

                {/* ===== Positive adjustments — opt-out picker (amber) ===== */}
                {stockTakeImportPreview.details?.positive_adjustments?.length > 0 && (() => {
                  const positives = stockTakeImportPreview.details.positive_adjustments;
                  const totalCount = positives.length;
                  const excludedCount = positives.filter((p) => stockTakeImportExcludedCodes.includes(p.item_code)).length;
                  const includedCount = totalCount - excludedCount;
                  const withheldUnits = positives
                    .filter((p) => stockTakeImportExcludedCodes.includes(p.item_code))
                    .reduce((sum, p) => sum + (p.delta || 0), 0);
                  return (
                    <Accordion
                      sx={{ mb: 1, border: 1, borderColor: 'warning.light', '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'warning.lighter' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WarningAmber color="warning" fontSize="small" />
                          <Typography variant="subtitle2">
                            Positive adjustments — stock increases ({(stockTakeImportPreview.summary?.positive_adjustments ?? totalCount).toLocaleString()})
                          </Typography>
                          <Chip label={(stockTakeImportPreview.summary?.positive_adjustments ?? totalCount).toLocaleString()} size="small" color="warning" />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                          Tick the increases you want to apply. Unticked ones are skipped — stock stays at its
                          current value. Decreases and other rows are unaffected.
                        </Alert>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setStockTakeImportExcludedCodes([])}
                          >
                            Include all
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => setStockTakeImportExcludedCodes(positives.map((p) => p.item_code))}
                          >
                            Exclude all
                          </Button>
                          <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
                            Including {includedCount.toLocaleString()} of {totalCount.toLocaleString()} increases
                            · excluding {excludedCount.toLocaleString()} (+{withheldUnits.toLocaleString()} units withheld)
                          </Typography>
                        </Box>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell><strong>Stock Code</strong></TableCell>
                                <TableCell><strong>Item Name</strong></TableCell>
                                <TableCell align="right"><strong>Counted</strong></TableCell>
                                <TableCell align="right"><strong>Current</strong></TableCell>
                                <TableCell align="right"><strong>New stock</strong></TableCell>
                                <TableCell align="right"><strong>Delta</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {positives.map((row, i) => {
                                const included = !stockTakeImportExcludedCodes.includes(row.item_code);
                                return (
                                  <TableRow key={row.item_code ?? i} hover>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        size="small"
                                        checked={included}
                                        onChange={() => handleStockTakeImportToggleExclude(row.item_code)}
                                      />
                                    </TableCell>
                                    <TableCell><strong>{row.item_code}</strong></TableCell>
                                    <TableCell>{row.item_name}</TableCell>
                                    <TableCell align="right">{row.counted_qty?.toLocaleString()}</TableCell>
                                    <TableCell align="right">{row.current_stock?.toLocaleString()}</TableCell>
                                    <TableCell align="right"><strong>{row.new_stock?.toLocaleString()}</strong></TableCell>
                                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                                      +{row.delta?.toLocaleString()}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}

                {/* ===== Blocking issues (red) ===== */}
                {stockTakeImportPreview.details?.unknown_codes?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'error',
                    icon: <ErrorOutline color="error" fontSize="small" />,
                    title: `Unknown item codes (${(stockTakeImportPreview.summary?.unknown_codes ?? stockTakeImportPreview.details.unknown_codes.length).toLocaleString()})`,
                    count: stockTakeImportPreview.summary?.unknown_codes ?? stockTakeImportPreview.details.unknown_codes.length,
                    defaultExpanded: true,
                    children: (() => {
                      const unknown = stockTakeImportPreview.details.unknown_codes;
                      const createCount = unknown.filter(
                        (u) => !stockTakeImportSkipCreateCodes.includes(u.stock_code)
                      ).length;
                      return (
                      <>
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                          These codes do not exist in GCS and block the import. Export them to review,
                          or create them as new items (their stock is set to the counted quantity).
                        </Alert>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={downloading === 'stock-take-unknown' ? <CircularProgress size={16} /> : <Download />}
                            onClick={handleDownloadUnknownCodes}
                            disabled={downloading === 'stock-take-unknown'}
                          >
                            Export to Excel
                          </Button>
                          <Box sx={{ flexGrow: 1 }} />
                          <TextField
                            select
                            size="small"
                            label="Create as"
                            value={stockTakeImportCreateType}
                            onChange={(e) => setStockTakeImportCreateType(e.target.value)}
                            sx={{ minWidth: 160 }}
                            SelectProps={{ native: true }}
                          >
                            <option value="raw_material">Raw Material</option>
                            <option value="manufactured">Manufactured Part</option>
                            <option value="assembly">Assembly</option>
                            <option value="finished_good">Finished Product</option>
                          </TextField>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={downloading === 'stock-take-create' ? <CircularProgress size={16} color="inherit" /> : <Add />}
                            onClick={handleCreateMissingItems}
                            disabled={downloading === 'stock-take-create' || createCount === 0}
                          >
                            Create {createCount} new item{createCount === 1 ? '' : 's'}
                          </Button>
                        </Box>
                        {renderStockTakeTable(unknown, [
                          {
                            key: '_create',
                            label: 'Create',
                            align: 'center',
                            render: (r) => (
                              <Checkbox
                                size="small"
                                checked={!stockTakeImportSkipCreateCodes.includes(r.stock_code)}
                                onChange={() => handleStockTakeImportToggleCreate(r.stock_code)}
                              />
                            ),
                          },
                          { key: 'sheet', label: 'Sheet' },
                          { key: 'stock_code', label: 'Stock Code', render: (r) => <strong>{r.stock_code}</strong> },
                          { key: 'item_name', label: 'Item Name (from file)' },
                          { key: 'counted_qty', label: 'Counted', align: 'right' },
                        ])}
                      </>
                      );
                    })(),
                  })}

                {stockTakeImportPreview.details?.duplicate_codes?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'error',
                    icon: <ErrorOutline color="error" fontSize="small" />,
                    title: `Duplicate codes in file (${(stockTakeImportPreview.summary?.duplicate_codes ?? stockTakeImportPreview.details.duplicate_codes.length).toLocaleString()})`,
                    count: stockTakeImportPreview.summary?.duplicate_codes ?? stockTakeImportPreview.details.duplicate_codes.length,
                    defaultExpanded: true,
                    children: (
                      <>
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                          The same stock code appears more than once. Remove duplicates before applying.
                        </Alert>
                        {renderStockTakeTable(stockTakeImportPreview.details.duplicate_codes, [
                          { key: 'sheet', label: 'Sheet' },
                          { key: 'row', label: 'Row', align: 'right' },
                          { key: 'stock_code', label: 'Stock Code', render: (r) => <strong>{r.stock_code}</strong> },
                        ])}
                      </>
                    ),
                  })}

                {/* Row-level errors that block apply */}
                {stockTakeImportPreview.errors?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'error',
                    icon: <ErrorOutline color="error" fontSize="small" />,
                    title: `Row errors (${stockTakeImportPreview.errors.length.toLocaleString()})`,
                    count: stockTakeImportPreview.errors.length,
                    defaultExpanded: true,
                    children: (
                      <>
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                          These rows could not be processed and block the import.
                        </Alert>
                        {renderStockTakeTable(stockTakeImportPreview.errors, [
                          { key: 'sheet', label: 'Sheet' },
                          { key: 'row', label: 'Row', align: 'right' },
                          { key: 'stock_code', label: 'Stock Code', render: (r) => <strong>{r.stock_code || '(no code)'}</strong> },
                          { key: 'error', label: 'Error' },
                        ])}
                      </>
                    ),
                  })}

                {/* Blocking note */}
                {stockTakeImportPreview.ready_to_apply === false && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Fix the blocking issues above before this can be applied (apply is all-or-nothing).
                  </Alert>
                )}

                {/* ===== Warnings (amber, do not block) ===== */}
                {stockTakeImportPreview.details?.negative_items?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'warning',
                    icon: <WarningAmber color="warning" fontSize="small" />,
                    title: `Will go negative (${(stockTakeImportPreview.summary?.negative_items ?? stockTakeImportPreview.details.negative_items.length).toLocaleString()})`,
                    count: stockTakeImportPreview.summary?.negative_items ?? stockTakeImportPreview.details.negative_items.length,
                    children: (
                      <>
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                          After applying, these items end up below zero. This does not block the import.
                        </Alert>
                        {renderStockTakeTable(stockTakeImportPreview.details.negative_items, [
                          { key: 'item_code', label: 'Stock Code', render: (r) => <strong>{r.item_code}</strong> },
                          { key: 'item_name', label: 'Item Name' },
                          { key: 'counted_qty', label: 'Counted', align: 'right', render: (r) => r.counted_qty?.toLocaleString() },
                          { key: 'current_stock', label: 'Current', align: 'right', render: (r) => r.current_stock?.toLocaleString() },
                          {
                            key: 'real_net_since_count', label: 'Net since count', align: 'right',
                            render: (r) => `${r.real_net_since_count > 0 ? '+' : ''}${r.real_net_since_count?.toLocaleString()}`,
                            cellSx: (r) => ({ color: r.real_net_since_count < 0 ? 'error.main' : r.real_net_since_count > 0 ? 'success.main' : 'inherit' }),
                          },
                          {
                            key: 'new_stock', label: 'New stock', align: 'right',
                            render: (r) => <strong>{r.new_stock?.toLocaleString()}</strong>,
                            cellSx: () => ({ color: 'error.main' }),
                          },
                        ])}
                      </>
                    ),
                  })}

                {stockTakeImportPreview.details?.reimport_items?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'info',
                    icon: <InfoOutlined color="info" fontSize="small" />,
                    title: `Already imported for this count date (${(stockTakeImportPreview.summary?.reimport_items ?? stockTakeImportPreview.details.reimport_items.length).toLocaleString()})`,
                    count: stockTakeImportPreview.summary?.reimport_items ?? stockTakeImportPreview.details.reimport_items.length,
                    children: (
                      <>
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                          These items already have a stock take for this date. Re-applying is safe — unchanged items
                          write nothing; only genuine differences post a correcting adjustment.
                        </Alert>
                        {renderStockTakeTable(stockTakeImportPreview.details.reimport_items, [
                          { key: 'item_code', label: 'Stock Code', render: (r) => <strong>{r.item_code}</strong> },
                          { key: 'item_name', label: 'Item Name' },
                          {
                            key: 'delta', label: 'Delta', align: 'right',
                            render: (r) => `${r.delta > 0 ? '+' : ''}${r.delta?.toLocaleString()}`,
                            cellSx: (r) => ({ color: r.delta > 0 ? 'success.main' : r.delta < 0 ? 'error.main' : 'text.secondary', fontWeight: 600 }),
                          },
                        ])}
                      </>
                    ),
                  })}

                {stockTakeImportPreview.details?.older_count_overwrites?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'warning',
                    icon: <WarningAmber color="warning" fontSize="small" />,
                    title: `Older count overwrites a newer one (${(stockTakeImportPreview.summary?.older_count_overwrites ?? stockTakeImportPreview.details.older_count_overwrites.length).toLocaleString()})`,
                    count: stockTakeImportPreview.summary?.older_count_overwrites ?? stockTakeImportPreview.details.older_count_overwrites.length,
                    children: (
                      <>
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                          A more recent stock take already exists for these items. Applying this older count will
                          overwrite the newer figure.
                        </Alert>
                        {renderStockTakeTable(stockTakeImportPreview.details.older_count_overwrites, [
                          { key: 'item_code', label: 'Stock Code', render: (r) => <strong>{r.item_code}</strong> },
                          { key: 'item_name', label: 'Item Name' },
                          {
                            key: 'prior_count_date', label: 'Already applied',
                            render: (r) => `a stock take dated ${r.prior_count_date} already applied`,
                          },
                          {
                            key: 'delta', label: 'Delta', align: 'right',
                            render: (r) => `${r.delta > 0 ? '+' : ''}${r.delta?.toLocaleString()}`,
                            cellSx: (r) => ({ color: r.delta > 0 ? 'success.main' : r.delta < 0 ? 'error.main' : 'text.secondary', fontWeight: 600 }),
                          },
                        ])}
                      </>
                    ),
                  })}

                {stockTakeImportPreview.details?.not_in_export_set?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'warning',
                    icon: <WarningAmber color="warning" fontSize="small" />,
                    title: `Not in stock-take item set (${(stockTakeImportPreview.summary?.not_in_export_set ?? stockTakeImportPreview.details.not_in_export_set.length).toLocaleString()})`,
                    count: stockTakeImportPreview.summary?.not_in_export_set ?? stockTakeImportPreview.details.not_in_export_set.length,
                    children: (
                      <>
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                          These items will apply but aren't part of the BOM / customer-order raw-material set.
                        </Alert>
                        {renderStockTakeTable(stockTakeImportPreview.details.not_in_export_set, [
                          { key: 'item_code', label: 'Stock Code', render: (r) => <strong>{r.item_code}</strong> },
                          { key: 'item_name', label: 'Item Name' },
                          { key: 'counted_qty', label: 'Counted', align: 'right', render: (r) => r.counted_qty?.toLocaleString() },
                          { key: 'current_stock', label: 'Current', align: 'right', render: (r) => r.current_stock?.toLocaleString() },
                          { key: 'new_stock', label: 'New stock', align: 'right', render: (r) => r.new_stock?.toLocaleString() },
                          {
                            key: 'delta', label: 'Delta', align: 'right',
                            render: (r) => `${r.delta > 0 ? '+' : ''}${r.delta?.toLocaleString()}`,
                            cellSx: (r) => ({ color: r.delta > 0 ? 'success.main' : r.delta < 0 ? 'error.main' : 'text.secondary', fontWeight: 600 }),
                          },
                        ])}
                      </>
                    ),
                  })}

                {/* ===== Informational (neutral, collapsed) ===== */}
                {stockTakeImportPreview.details?.post_count_movement?.length > 0 &&
                  renderStockTakeSection({
                    tone: 'info',
                    icon: <InfoOutlined color="info" fontSize="small" />,
                    title: `Transactions applied after the count (${(stockTakeImportPreview.summary?.post_count_movement ?? stockTakeImportPreview.details.post_count_movement.length).toLocaleString()})`,
                    count: stockTakeImportPreview.summary?.post_count_movement ?? stockTakeImportPreview.details.post_count_movement.length,
                    children: (
                      <>
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                          Stock transactions logged after the stock-take date were folded into the new figure, so
                          post-count activity isn't lost.
                        </Alert>
                        {renderStockTakeTable(stockTakeImportPreview.details.post_count_movement, [
                          { key: 'item_code', label: 'Stock Code', render: (r) => <strong>{r.item_code}</strong> },
                          { key: 'item_name', label: 'Item Name' },
                          {
                            key: 'real_net_since_count', label: 'Net since count', align: 'right',
                            render: (r) => `${r.real_net_since_count > 0 ? '+' : ''}${r.real_net_since_count?.toLocaleString()}`,
                            cellSx: (r) => ({ color: r.real_net_since_count < 0 ? 'error.main' : r.real_net_since_count > 0 ? 'success.main' : 'inherit' }),
                          },
                          { key: 'counted_qty', label: 'Counted', align: 'right', render: (r) => r.counted_qty?.toLocaleString() },
                          { key: 'new_stock', label: 'New stock', align: 'right', render: (r) => r.new_stock?.toLocaleString() },
                        ])}
                      </>
                    ),
                  })}

                {/* Changes preview */}
                {stockTakeImportPreview.preview?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Top changes (sorted by absolute delta)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Sheet</strong></TableCell>
                            <TableCell><strong>Item</strong></TableCell>
                            <TableCell><strong>Counted At</strong></TableCell>
                            <TableCell align="right"><strong>Counted</strong></TableCell>
                            <TableCell align="right"><strong>Current</strong></TableCell>
                            <TableCell align="right"><strong>Real Net Since</strong></TableCell>
                            <TableCell align="right"><strong>New Stock</strong></TableCell>
                            <TableCell align="right"><strong>Δ</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockTakeImportPreview.preview.map((c, i) => (
                            <TableRow key={i}>
                              <TableCell>{c.sheet}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{c.item_code}</Typography>
                                <Typography variant="caption" color="text.secondary">{c.item_name}</Typography>
                                {c.warning && (
                                  <Typography variant="caption" color="warning.main" display="block">
                                    ⚠ {c.warning}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{c.count_timestamp}</TableCell>
                              <TableCell align="right">{c.counted_qty.toLocaleString()}</TableCell>
                              <TableCell align="right">{c.current_stock.toLocaleString()}</TableCell>
                              <TableCell align="right" sx={{ color: c.real_net_since_count < 0 ? 'error.main' : c.real_net_since_count > 0 ? 'success.main' : 'inherit' }}>
                                {c.real_net_since_count > 0 ? '+' : ''}{c.real_net_since_count.toLocaleString()}
                              </TableCell>
                              <TableCell align="right">{c.new_stock.toLocaleString()}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight={600}
                                  color={c.delta > 0 ? 'success.main' : c.delta < 0 ? 'error.main' : 'text.secondary'}
                                >
                                  {c.delta > 0 ? '+' : ''}{c.delta.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {stockTakeImportPreview.summary?.rows_to_apply > stockTakeImportPreview.preview.length && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Showing top {stockTakeImportPreview.preview.length} of {stockTakeImportPreview.summary.rows_to_apply} rows. All rows will be applied.
                      </Alert>
                    )}
                  </>
                )}

                {stockTakeImportPreview.summary?.rows_skipped_blank > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {stockTakeImportPreview.summary.rows_skipped_blank} row{stockTakeImportPreview.summary.rows_skipped_blank === 1 ? '' : 's'} skipped (blank Date and Actual).
                  </Alert>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CloudUpload sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Select a completed Stock Take xlsx to preview the changes.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={resetStockTakeImport}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={downloading === 'stock-take-import' ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
              onClick={handleApplyStockTakeImport}
              disabled={
                downloading === 'stock-take-import' ||
                !stockTakeImportPreview ||
                !stockTakeImportPreview.ready_to_apply ||
                stockTakeImportPreview.summary?.rows_to_apply === 0 ||
                (stockTakeImportAvailableSheets.length > 0 && stockTakeImportSelectedSheets.length === 0)
              }
              data-testid="stock-take-import-apply-btn"
            >
              {downloading === 'stock-take-import' ? 'Applying...' : 'Apply to GCS'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transactions Since Stock Take (stocktake-since) Dialog */}
        <Dialog
          open={stockTakeSinceDialogOpen}
          onClose={() => setStockTakeSinceDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Transactions Since Stock Take</Typography>
                <Typography variant="caption" color="text.secondary">
                  Per-item list of every real stock transaction logged after the stock take date — the post-count movements a Stock Take Import folds in.
                </Typography>
              </Box>
              <IconButton onClick={() => setStockTakeSinceDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Select the stock take date (and an optional end date)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Stock take date"
                    value={stockTakeSinceStartDate}
                    onChange={(newValue) => setStockTakeSinceStartDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!stockTakeSinceErrors.startDate,
                        helperText: stockTakeSinceErrors.startDate
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Up to (end date)"
                    value={stockTakeSinceEndDate}
                    onChange={(newValue) => setStockTakeSinceEndDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!stockTakeSinceErrors.endDate,
                        helperText: stockTakeSinceErrors.endDate || 'Optional — defaults to now'
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleStockTakeSincePreview}
                  disabled={previewLoading}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <Visibility />}
                >
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {stockTakeSincePreview?.error ? (
              <Alert severity="error">{stockTakeSincePreview.error}</Alert>
            ) : stockTakeSincePreview ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {stockTakeSincePreview.summary?.items_with_movements?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Items with Movements</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {stockTakeSincePreview.summary?.total_transactions?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Transactions</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {stockTakeSincePreview.summary?.restricted_to_export_set && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Restricted to the stock-take export set.
                  </Alert>
                )}

                {(stockTakeSincePreview.items?.length || 0) === 0 ? (
                  <Alert severity="info">
                    No stock transactions found after the selected stock take date.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Items</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Stock Code</strong></TableCell>
                            <TableCell><strong>Item Name</strong></TableCell>
                            <TableCell align="right"><strong>Transactions</strong></TableCell>
                            <TableCell align="right"><strong>Net Change</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stockTakeSincePreview.items?.map((item, index) => (
                            <TableRow key={item.item_id ?? index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{item.item_code}</Typography>
                              </TableCell>
                              <TableCell>{item.item_name}</TableCell>
                              <TableCell align="right">{item.transaction_count?.toLocaleString() || 0}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight={600}
                                  color={item.net_change > 0 ? 'success.main' : item.net_change < 0 ? 'error.main' : 'text.secondary'}
                                >
                                  {item.net_change > 0 ? '+' : ''}{item.net_change?.toLocaleString() || 0}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {stockTakeSincePreview.truncated && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Preview capped at {stockTakeSincePreview.items?.length || 0} of {stockTakeSincePreview.items_total} items. Download for the full list.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarMonth sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Pick the stock take date, then click Preview.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStockTakeSinceDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={downloading === 'stock-take-transactions-since' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadStockTakeSince}
              disabled={
                downloading === 'stock-take-transactions-since' ||
                !stockTakeSinceStartDate
              }
            >
              {downloading === 'stock-take-transactions-since' ? 'Generating...' : 'Download Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Inventory Value on Date (single-date) Dialog */}
        <Dialog
          open={inventoryValueDialogOpen}
          onClose={() => setInventoryValueDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Inventory Value on Date</Typography>
                <Typography variant="caption" color="text.secondary">
                  Stock at the end of the chosen date, valued at each item's Sage last cost price (standard cost when no last cost is set).
                </Typography>
              </Box>
              <IconButton onClick={() => setInventoryValueDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Select the valuation date
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Valuation date"
                    value={inventoryValueDate}
                    onChange={(newValue) => setInventoryValueDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!inventoryValueErrors.date,
                        helperText: inventoryValueErrors.date || 'Stock is valued as at the end of this day'
                      }
                    }}
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                sx={{ mt: 1, display: 'flex' }}
                control={
                  <Checkbox
                    checked={inventoryValueOnlyCounted}
                    onChange={(e) => setInventoryValueOnlyCounted(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Only items counted in a stock take (excludes untouched placeholder stock)
                  </Typography>
                }
              />
              {alwaysIncludeField('inventory-value')}
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleInventoryValuePreview}
                  disabled={previewLoading}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <Visibility />}
                >
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {inventoryValuePreview?.error ? (
              <Alert severity="error">{inventoryValuePreview.error}</Alert>
            ) : inventoryValuePreview ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        £{inventoryValuePreview.summary?.total_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Inventory Value</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {inventoryValuePreview.summary?.item_count?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Items with Stock</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {inventoryValuePreview.summary?.items_without_last_cost?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Items without a Last Cost</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {(inventoryValuePreview.items?.length || 0) === 0 ? (
                  <Alert severity="info">
                    No items had stock at the end of the selected date.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Highest-value items</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Item Code</strong></TableCell>
                            <TableCell><strong>Item Name</strong></TableCell>
                            <TableCell align="right"><strong>Quantity</strong></TableCell>
                            <TableCell align="right"><strong>Unit Cost</strong></TableCell>
                            <TableCell><strong>Cost Source</strong></TableCell>
                            <TableCell align="right"><strong>Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventoryValuePreview.items?.map((item, index) => (
                            <TableRow key={item.item_code ?? index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{item.item_code}</Typography>
                              </TableCell>
                              <TableCell>{item.item_name}</TableCell>
                              <TableCell align="right">{item.quantity?.toLocaleString() || 0}</TableCell>
                              <TableCell align="right">£{item.unit_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</TableCell>
                              <TableCell>{item.cost_source}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>
                                  £{item.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {inventoryValuePreview.truncated && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Preview capped at {inventoryValuePreview.items?.length || 0} of {inventoryValuePreview.items_total} items. Download for the full list.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarMonth sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Pick the valuation date, then click Preview.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInventoryValueDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={downloading === 'inventory-value' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadInventoryValue}
              disabled={
                downloading === 'inventory-value' ||
                !inventoryValueDate
              }
            >
              {downloading === 'inventory-value' ? 'Generating...' : 'Download Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* BOM Usage by Month Dialog */}
        <Dialog
          open={bomUsageDialogOpen}
          onClose={() => setBomUsageDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">BOM Usage by Month</Typography>
                <Typography variant="caption" color="text.secondary">
                  How many of each BOM was used in the month — ordered and shipped quantities per item with an active BOM, from customer orders placed in the month (cancelled orders excluded).
                </Typography>
              </Box>
              <IconButton onClick={() => setBomUsageDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Select the month
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Month"
                    views={['year', 'month']}
                    openTo="month"
                    value={bomUsageMonth}
                    onChange={(newValue) => setBomUsageMonth(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: 'Orders placed in this calendar month'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CalendarMonth />}
                    onClick={() => setBomUsageMonth(new Date())}
                  >
                    Current month
                  </Button>
                </Grid>
              </Grid>
              {alwaysIncludeField('bom-usage')}
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBomUsagePreview}
                  disabled={previewLoading || !bomUsageMonth}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <Visibility />}
                >
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {bomUsagePreview?.error ? (
              <Alert severity="error">{bomUsagePreview.error}</Alert>
            ) : bomUsagePreview ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {bomUsagePreview.summary?.bom_count?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">BOMs Used</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {bomUsagePreview.summary?.order_count?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Orders</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {bomUsagePreview.summary?.total_qty_ordered?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Qty Ordered</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {bomUsagePreview.summary?.total_qty_shipped?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Qty Shipped</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {(bomUsagePreview.items?.length || 0) === 0 ? (
                  <Alert severity="info">
                    No BOM items were ordered in the selected month.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Most-used BOMs</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Item Code</strong></TableCell>
                            <TableCell><strong>Item Name</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell align="right"><strong>Orders</strong></TableCell>
                            <TableCell align="right"><strong>Qty Ordered</strong></TableCell>
                            <TableCell align="right"><strong>Qty Shipped</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bomUsagePreview.items?.map((item, index) => (
                            <TableRow key={item.item_code ?? index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{item.item_code}</Typography>
                              </TableCell>
                              <TableCell>{item.item_name}</TableCell>
                              <TableCell>{item.item_type}</TableCell>
                              <TableCell align="right">{item.order_count?.toLocaleString() || 0}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>
                                  {item.qty_ordered?.toLocaleString() || 0}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{item.qty_shipped?.toLocaleString() || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {bomUsagePreview.truncated && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Preview capped at {bomUsagePreview.items?.length || 0} of {bomUsagePreview.items_total} BOMs. Download for the full list.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarMonth sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Pick a month, then click Preview.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBomUsageDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={downloading === 'bom-usage' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadBomUsage}
              disabled={downloading === 'bom-usage' || !bomUsageMonth}
            >
              {downloading === 'bom-usage' ? 'Generating...' : 'Download Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Raw Material Usage Dialog */}
        <Dialog
          open={rawMaterialUsageDialogOpen}
          onClose={() => setRawMaterialUsageDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Raw Material Usage</Typography>
                <Typography variant="caption" color="text.secondary">
                  Total quantity of each raw material used between two dates, by part number and name - consumed in production (work orders) plus shipped direct to customers, from the stock transaction ledger.
                </Typography>
              </Box>
              <IconButton onClick={() => setRawMaterialUsageDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Select the date range
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <DatePicker
                    label="From"
                    value={rawMaterialUsageStartDate}
                    onChange={(newValue) => setRawMaterialUsageStartDate(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DatePicker
                    label="To"
                    value={rawMaterialUsageEndDate}
                    onChange={(newValue) => setRawMaterialUsageEndDate(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CalendarMonth />}
                    onClick={() => {
                      setRawMaterialUsageStartDate(new Date(new Date().getFullYear(), 0, 1));
                      setRawMaterialUsageEndDate(new Date());
                    }}
                  >
                    This year
                  </Button>
                </Grid>
              </Grid>
              {alwaysIncludeField('raw-material-usage')}
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleRawMaterialUsagePreview}
                  disabled={previewLoading || !rawMaterialUsageStartDate || !rawMaterialUsageEndDate}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <Visibility />}
                >
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {rawMaterialUsagePreview?.error ? (
              <Alert severity="error">{rawMaterialUsagePreview.error}</Alert>
            ) : rawMaterialUsagePreview ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {rawMaterialUsagePreview.summary?.item_count?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Raw Materials Used</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {rawMaterialUsagePreview.summary?.total_consumed?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Consumed in Production</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {rawMaterialUsagePreview.summary?.total_shipped?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Shipped Direct</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {rawMaterialUsagePreview.summary?.total_usage?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Usage</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {(rawMaterialUsagePreview.items?.length || 0) === 0 ? (
                  <Alert severity="info">
                    No raw material usage was recorded in the selected date range.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>Most-used raw materials</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Part Number</strong></TableCell>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>UoM</strong></TableCell>
                            <TableCell align="right"><strong>Consumed in Production</strong></TableCell>
                            <TableCell align="right"><strong>Shipped Direct</strong></TableCell>
                            <TableCell align="right"><strong>Total Usage</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rawMaterialUsagePreview.items?.map((item, index) => (
                            <TableRow key={item.item_code ?? index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{item.item_code}</Typography>
                              </TableCell>
                              <TableCell>{item.item_name}</TableCell>
                              <TableCell>{item.unit_of_measure}</TableCell>
                              <TableCell align="right">{item.consumed_production?.toLocaleString() || 0}</TableCell>
                              <TableCell align="right">{item.shipped_direct?.toLocaleString() || 0}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>
                                  {item.total_usage?.toLocaleString() || 0}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {rawMaterialUsagePreview.truncated && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Preview capped at {rawMaterialUsagePreview.items?.length || 0} of {rawMaterialUsagePreview.items_total} raw materials. Download for the full list.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarMonth sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Pick a date range, then click Preview.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRawMaterialUsageDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={downloading === 'raw-material-usage' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadRawMaterialUsage}
              disabled={downloading === 'raw-material-usage' || !rawMaterialUsageStartDate || !rawMaterialUsageEndDate}
            >
              {downloading === 'raw-material-usage' ? 'Generating...' : 'Download Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* BOM Cost Roll-Up Dialog */}
        <Dialog
          open={bomCostDialogOpen}
          onClose={() => setBomCostDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">BOM Cost</Typography>
                <Typography variant="caption" color="text.secondary">
                  Explodes the full BOM tree and sums every bottom-level component, valued at Sage last cost price (standard cost when no last cost is set).
                </Typography>
              </Box>
              <IconButton onClick={() => setBomCostDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Search for the BOM to cost
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Autocomplete
                    options={bomCostOptions}
                    value={bomCostSelected}
                    onChange={(e, newValue) => { setBomCostSelected(newValue); setBomCostPreview(null); }}
                    onInputChange={(e, newInput) => setBomCostQuery(newInput)}
                    loading={bomCostSearchLoading}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight={500}>
                            {option.item_code} - {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.item_type}{option.is_top_level ? ' · Top-level BOM' : ' · Sub-assembly'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="BOM item"
                        placeholder="Search by item code or name..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {bomCostSearchLoading ? <CircularProgress size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Quantity to build"
                    type="number"
                    fullWidth
                    value={bomCostQuantity}
                    onChange={(e) => { setBomCostQuantity(e.target.value); setBomCostPreview(null); }}
                    error={!!bomCostQuantity && !bomCostQuantityValid}
                    helperText={!!bomCostQuantity && !bomCostQuantityValid ? 'Enter a number greater than zero' : 'Component quantities are scaled by this'}
                    inputProps={{ min: 0, step: 'any' }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBomCostPreview}
                  disabled={previewLoading || !bomCostSelected || !bomCostQuantityValid}
                  startIcon={previewLoading ? <CircularProgress size={20} /> : <Visibility />}
                >
                  {previewLoading ? 'Loading...' : 'Preview'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {bomCostPreview?.error ? (
              <Alert severity="error">{bomCostPreview.error}</Alert>
            ) : bomCostPreview ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        £{bomCostPreview.summary?.total_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total Material Cost</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        £{bomCostPreview.summary?.cost_per_unit?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }) || '0.00'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Cost per Unit</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {bomCostPreview.summary?.leaf_count?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Bottom-Level Components</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h5" fontWeight={600}>
                        {bomCostPreview.summary?.items_without_last_cost?.toLocaleString() || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Valued at Standard Cost</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {(bomCostPreview.summary?.warnings?.length || 0) > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {bomCostPreview.summary.warnings.map((w, i) => (
                      <div key={i}>{w}</div>
                    ))}
                  </Alert>
                )}

                {(bomCostPreview.components?.length || 0) === 0 ? (
                  <Alert severity="info">
                    No bottom-level components found for this BOM.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Bottom-level components for {bomCostPreview.quantity} × {bomCostPreview.bom?.item_code}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Item Code</strong></TableCell>
                            <TableCell><strong>Item Name</strong></TableCell>
                            <TableCell><strong>Type</strong></TableCell>
                            <TableCell align="right"><strong>Qty per Unit</strong></TableCell>
                            <TableCell align="right"><strong>Total Qty</strong></TableCell>
                            <TableCell align="right"><strong>Unit Cost</strong></TableCell>
                            <TableCell><strong>Cost Source</strong></TableCell>
                            <TableCell align="right"><strong>Line Cost</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bomCostPreview.components?.map((c, index) => (
                            <TableRow key={c.item_code ?? index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{c.item_code}</Typography>
                              </TableCell>
                              <TableCell>{c.item_name}</TableCell>
                              <TableCell>{c.item_type}</TableCell>
                              <TableCell align="right">{c.qty_per_unit?.toLocaleString(undefined, { maximumFractionDigits: 6 })}</TableCell>
                              <TableCell align="right">{c.total_quantity?.toLocaleString(undefined, { maximumFractionDigits: 6 })}</TableCell>
                              <TableCell align="right">£{c.unit_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }) || '0.00'}</TableCell>
                              <TableCell>{c.cost_source}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>
                                  £{c.line_cost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {bomCostPreview.truncated && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Preview capped at {bomCostPreview.components?.length || 0} of {bomCostPreview.components_total} components. Download for the full list.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AccountTree sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Search for a BOM, set the quantity, then click Preview.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBomCostDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="info"
              startIcon={downloading === 'bom-cost' ? <CircularProgress size={20} color="inherit" /> : <Download />}
              onClick={handleDownloadBomCost}
              disabled={
                downloading === 'bom-cost' ||
                !bomCostSelected ||
                !bomCostQuantityValid
              }
            >
              {downloading === 'bom-cost' ? 'Generating...' : 'Download Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Undo Last Stock Take confirmation Dialog */}
        <Dialog
          open={undoDialogOpen}
          onClose={resetUndoDialog}
          maxWidth="md"
          fullWidth
          data-testid="stock-take-undo-dialog"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Restore color="error" />
                <Typography variant="h6">Undo last stock take</Typography>
              </Box>
              <IconButton onClick={resetUndoDialog}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {undoPreview ? (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This will reverse the last stock take applied on {undoPreview.applied_at}.
                </Alert>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>{(undoPreview.transactions ?? 0).toLocaleString()}</strong> adjustment
                  {undoPreview.transactions === 1 ? '' : 's'} will be reversed · net change being undone:{' '}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 600,
                      color: undoPreview.net_change > 0
                        ? 'success.main'
                        : undoPreview.net_change < 0
                          ? 'error.main'
                          : 'text.secondary',
                    }}
                  >
                    {undoPreview.net_change > 0 ? '+' : ''}{(undoPreview.net_change ?? 0).toLocaleString()}
                  </Box>{' '}
                  · {(undoPreview.items_increased ?? 0).toLocaleString()} up /{' '}
                  {(undoPreview.items_decreased ?? 0).toLocaleString()} down.
                </Typography>

                {(undoPreview.preview?.length || 0) > 0 ? (
                  <>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Stock Code</strong></TableCell>
                            <TableCell><strong>Item Name</strong></TableCell>
                            <TableCell align="right"><strong>Applied adjustment</strong></TableCell>
                            <TableCell align="right"><strong>Will reverse by</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {undoPreview.preview.map((row, i) => (
                            <TableRow key={row.item_code ?? i}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{row.item_code}</Typography>
                              </TableCell>
                              <TableCell>{row.item_name}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  color={row.adjustment > 0 ? 'success.main' : row.adjustment < 0 ? 'error.main' : 'text.secondary'}
                                >
                                  {row.adjustment > 0 ? '+' : ''}{(row.adjustment ?? 0).toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  color={row.will_reverse_by > 0 ? 'success.main' : row.will_reverse_by < 0 ? 'error.main' : 'text.secondary'}
                                >
                                  {row.will_reverse_by > 0 ? '+' : ''}{(row.will_reverse_by ?? 0).toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {undoPreview.transactions > undoPreview.preview.length && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Showing first {undoPreview.preview.length.toLocaleString()} of {undoPreview.transactions.toLocaleString()}.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Alert severity="info">No item-level detail available for this stock take.</Alert>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={resetUndoDialog} disabled={undoing}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              startIcon={undoing ? <CircularProgress size={20} color="inherit" /> : <Restore />}
              onClick={handleConfirmUndo}
              disabled={undoing || !undoPreview}
              data-testid="stock-take-undo-confirm-btn"
            >
              {undoing ? 'Undoing...' : 'Undo stock take'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;